import clsx from 'clsx';
import Image from 'next/image';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ImageCropModal } from '@/app/(main)/gallery/components/ImageCrop';
import { SelectGalleryImageModal } from '@/app/(main)/gallery/components/SelectGalleryImageModal';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { useModalState } from '@/hooks/useModalState';
import type { GalleryItemData } from '@/types/galleryApiTypes';
import PhotoIcon from '/public/icons/camera.svg';
import RefreshIcon from '/public/icons/refresh.svg';
import UploadIcon from '/public/icons/upload-cloud.svg';

interface UploadImageSectionProps {
  onUploadImage?: (
    file: File
  ) => Promise<{ profile_picture_url: string | null; profile_picture_gallery_id: number | null }>;
  onUploadImageFromGallery?: (imageId: number) => Promise<{ profile_picture_url: string | null }>;
  onDeleteImage?: () => void;
  userId?: number | null;
  isLoading?: boolean;
  isDeleteLoading?: boolean;
}

export const UploadImageSection = ({
  onUploadImage,
  onDeleteImage,
  onUploadImageFromGallery,
  isLoading,
  isDeleteLoading,
  userId,
}: UploadImageSectionProps) => {
  const { watch, setValue } = useFormContext();
  const { isOpen, open, close } = useModalState();
  const { isOpen: isCropModalOpen, open: openCropModal, close: closeCropModal } = useModalState();
  const profilePictureUrl = watch('primary_passenger.client.profile_picture_url');
  const [previewUrl, setPreviewUrl] = useState<string | null>(profilePictureUrl ?? null);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const handleDeleteImage = () => {
    onDeleteImage?.();

    setPreviewUrl(null);
    setValue('primary_passenger.client.profile_picture_url', null);
    setValue('primary_passenger.client.profile_picture_gallery_id', null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];

      if (file && file.type.startsWith('image/')) {
        const tempUrl = URL.createObjectURL(file);
        setImageToCrop(tempUrl);
        openCropModal();
      } else {
        setPreviewUrl(null);
        setValue('primary_passenger.client.profile_picture_url', null);
      }
    } catch {
      toast.error('Error uploading image');
      closeCropModal();
    }
  };

  const handleCropSave = async (blob: Blob) => {
    try {
      const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });

      const result = await onUploadImage?.(file);
      setValue('primary_passenger.client.profile_picture_url', result?.profile_picture_url ?? null);
      setValue('primary_passenger.client.profile_picture_gallery_id', result?.profile_picture_gallery_id ?? null);
      setPreviewUrl(result?.profile_picture_url ?? null);

      if (imageToCrop) {
        URL.revokeObjectURL(imageToCrop);
      }
      setImageToCrop(null);
      closeCropModal();
    } catch {
      toast.error('Error uploading image');
      closeCropModal();
    }
  };

  const handleCloseCropModal = () => {
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
    }
    setImageToCrop(null);
    closeCropModal();
  };

  const handleSelectImage = async (image: GalleryItemData) => {
    try {
      close();
      const result = await onUploadImageFromGallery?.(image.id);
      setValue('primary_passenger.client.profile_picture_url', result?.profile_picture_url ?? null);
      setPreviewUrl(result?.profile_picture_url ?? null);
    } catch {
      toast.error('Error loading image from gallery');
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-[24px] bg-blue-50 px-6 py-3 rounded-[14px]">
        <div className="flex flex-col gap-[2px] w-1/2">
          <h5 className="text-black">Profile Photo (Optional)</h5>
          <p className="text-body-small text-grey-700">You can upload a photo later from the client profile.</p>
        </div>
        <div className="flex gap-[16px] justify-end w-1/2 py-1 items-center">
          <div className="w-[60px] h-[60px] bg-white rounded-full flex items-center justify-center border border-grey-300 overflow-hidden">
            {previewUrl ? (
              <Image
                width={60}
                height={60}
                src={previewUrl}
                alt="Selected image"
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <PhotoIcon className="w-6 h-6 text-blue-900 stroke-current" />
            )}
          </div>
          <div className={clsx('flex flex-col items-center gap-[0px]', userId ? 'translate-y-2' : '')}>
            <BaseButton
              startIcon={previewUrl ? <RefreshIcon /> : <UploadIcon />}
              intent="primary"
              size="medium"
              type="button"
              onClick={handleUploadClick}
              disabled={isLoading}
            >
              {isLoading
                ? previewUrl
                  ? 'Replacing...'
                  : 'Uploading...'
                : previewUrl
                  ? 'Replace Image'
                  : 'Upload File'}
            </BaseButton>
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/gif, image/webp, image/bmp"
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            {previewUrl ? (
              <button
                onClick={handleDeleteImage}
                disabled={isDeleteLoading || isLoading}
                className="text-red-500 text-body-small font-medium cursor-pointer mt-1"
                type="button"
              >
                {isDeleteLoading ? 'Deleting...' : 'Delete Image'}
              </button>
            ) : userId ? (
              <BaseButton onClick={open} intent="ghost" size="medium" type="button">
                Choose from Gallery
              </BaseButton>
            ) : null}
          </div>
        </div>
      </div>
      {isOpen && (
        <SelectGalleryImageModal isOpen={isOpen} onClose={close} onSelect={handleSelectImage} userId={userId} />
      )}
      {isCropModalOpen && imageToCrop ? (
        <ImageCropModal
          isOpen={isCropModalOpen}
          circularCrop
          onClose={handleCloseCropModal}
          imageUrl={imageToCrop}
          onSave={handleCropSave}
          title="Crop  Image"
        />
      ) : null}
    </>
  );
};
